<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-payment-method-title"
      @click.self="close"
    >
      <div class="w-full max-w-sm rounded-lg bg-white border border-border shadow-lg overflow-hidden">
        <!-- Body -->
        <div class="px-6 pt-6 pb-5 flex gap-4">
          <!-- Icon -->
          <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-50 border border-red-100">
            <Trash2 class="w-4 h-4 text-red-500" />
          </div>
          <!-- Content -->
          <div class="min-w-0">
            <h3 id="delete-payment-method-title" class="text-sm font-semibold text-gray-900">
              Remove payment method?
            </h3>
            <p class="mt-1 text-sm text-muted-foreground leading-snug">
              Card ending in <span class="font-semibold text-gray-900">{{ cardLast4 }}</span> will be permanently removed from your account.
            </p>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-2 border-t border-border bg-gray-50/60 px-5 py-3">
          <button
            type="button"
            ref="cancelButtonRef"
            class="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            @click="close"
          >
            Cancel
          </button>
          <button
            type="button"
            ref="confirmButtonRef"
            class="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
            :disabled="loading"
            @click="$emit('confirm')"
          >
            <svg
              v-if="loading"
              class="h-3.5 w-3.5 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Remove
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount, nextTick } from 'vue'
import { Trash2 } from 'lucide-vue-next'

type Props = {
  visible: boolean
  cardLast4?: string
  loading?: boolean
}

type Emits = {
  (e: 'close'): void
  (e: 'confirm'): void
}

const props = withDefaults(defineProps<Props>(), {
  cardLast4: '',
  loading: false
})

const emit = defineEmits<Emits>()

const cancelButtonRef = ref<HTMLButtonElement | null>(null)
const confirmButtonRef = ref<HTMLButtonElement | null>(null)

const close = () => emit('close')

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    event.preventDefault()
    close()
    return
  }

  if (event.key !== 'Tab') return

  const focusables = [cancelButtonRef.value, confirmButtonRef.value].filter(Boolean) as HTMLButtonElement[]
  if (focusables.length === 0) return

  const first = focusables[0]
  const last = focusables[focusables.length - 1]
  const active = document.activeElement as HTMLElement | null

  if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  } else if (event.shiftKey && active === first) {
    event.preventDefault()
    last.focus()
  }
}

watch(
  () => props.visible,
  async (visible) => {
    if (visible) {
      await nextTick()
      cancelButtonRef.value?.focus()
      document.addEventListener('keydown', handleKeydown)
    } else {
      document.removeEventListener('keydown', handleKeydown)
    }
  }
)

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>
